import { useState } from "react";
import { Globe, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface ResearchSource {
  title?: string;
  url: string;
  snippet?: string;
}

export interface ResearchInfo {
  requested: boolean;
  country?: string;
  status: "ok" | "empty" | "error";
  summary?: string;
  sources?: ResearchSource[];
  raw?: unknown;
}

interface Props {
  info: ResearchInfo;
  className?: string;
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function ResearchPanel({ info, className }: Props) {
  const [open, setOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  if (!info.requested) return null;

  const sourcesCount = info.sources?.length ?? 0;
  const hasSummary = Boolean(info.summary && info.summary.trim().length > 0);

  const badge = (() => {
    if (info.status === "ok") {
      return (
        <Badge variant="outline" className="gap-1 border-green-500/40 text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-3 w-3" />
          Research: pobrano{sourcesCount > 0 ? ` (${sourcesCount} ${sourcesCount === 1 ? "źródło" : sourcesCount < 5 ? "źródła" : "źródeł"})` : ""}
        </Badge>
      );
    }
    if (info.status === "empty") {
      return (
        <Badge variant="outline" className="gap-1 border-yellow-500/40 text-yellow-700 dark:text-yellow-400">
          <AlertTriangle className="h-3 w-3" />
          Research: brak danych
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 border-destructive/50 text-destructive">
        <XCircle className="h-3 w-3" />
        Research: błąd
      </Badge>
    );
  })();

  const canExpand = hasSummary || sourcesCount > 0 || info.raw !== undefined;

  return (
    <div className={cn("rounded-md border bg-muted/30 text-sm", className)}>
      <button
        type="button"
        onClick={() => canExpand && setOpen((o) => !o)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 text-left",
          canExpand ? "cursor-pointer hover:bg-muted/50" : "cursor-default"
        )}
      >
        <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        {badge}
        {info.country && (
          <span className="text-xs text-muted-foreground">· {info.country}</span>
        )}
        {canExpand && (
          <span className="ml-auto text-muted-foreground">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        )}
      </button>

      {open && canExpand && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t">
          {hasSummary && (
            <div className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
              {info.summary}
            </div>
          )}

          {sourcesCount > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">Źródła</div>
              <ul className="space-y-1">
                {info.sources!.map((s, i) => (
                  <li key={i} className="text-xs">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                    >
                      <ExternalLink className="h-3 w-3 shrink-0" />
                      <span>{s.title || hostOf(s.url)}</span>
                      {s.title && <span className="text-muted-foreground">({hostOf(s.url)})</span>}
                    </a>
                    {s.snippet && (
                      <div className="text-muted-foreground mt-0.5 line-clamp-2">{s.snippet}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {info.raw !== undefined && (
            <div>
              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                {showRaw ? "Ukryj surowe dane" : "Pokaż surowe dane"}
              </button>
              {showRaw && (
                <pre className="mt-2 max-h-64 overflow-auto rounded bg-background/60 p-2 text-[10px] leading-tight">
                  {JSON.stringify(info.raw, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
